import * as THREE from 'three'
import gsap from 'gsap'

// Benötigte Imports aus scene.js
import { 
    scene, camera, renderer, controls, canvas, 
    model, hotspotsGroup, hotspotMaterial, sizes 
} from './scene.js'

// DOM-Elemente
const fadeElements = document.querySelectorAll('.fade-target')
const hotspotLabels = document.querySelectorAll('.label')
const backButton = document.getElementById('back-to-all')
const infoWindow = document.getElementById('info-window')
const infoVideo = document.getElementById('info-video')
const infoTitle = document.getElementById('info-title')
const infoText = document.getElementById('info-text')
const infoInfo = document.getElementById('info-info')
const prevBtn = document.getElementById('prev-step')
const nextBtn = document.getElementById('next-step')
const step_indicator = document.getElementById('step-indicator')
const videoContainer = document.querySelector('.video-container')
const tourBtn = document.getElementById('start-tour-btn')

// Variablen
let hotspotData = {}
let tourData = {}
let tourKeys = []

let currentHotspotKey = null
let currentStepIndex = 0
let isTourMode = false
let currentTourStepKey = null

const raycaster = new THREE.Raycaster()
const mouse = new THREE.Vector2()

// JSON-Daten laden
fetch('./data.json')
    .then(response => response.json())
    .then(data => {
        hotspotData = data.hotspotData
        tourData = data.tourData
        tourKeys = Object.keys(tourData)
    })
    .catch(error => console.error("Fehler beim Laden der JSON-Daten:", error))

/*
 * Funktionen
 */

// Animate
const clock = new THREE.Clock()
const tick = () => {
    const elapsedTime = clock.getElapsedTime()

    // Puls-Animation für Hotspots
    if (hotspotsGroup.visible) {
        hotspotsGroup.children.forEach((hotspot) => {
            const pulse = 1 + Math.sin(elapsedTime * 3) * 0.2
            hotspot.scale.set(pulse, pulse, pulse)
        })
    }
    controls.update()

    // Labels positionieren
    if (hotspotsGroup.visible && !isTourMode) {
        hotspotsGroup.children.forEach((hotspot) => {
            const screenPosition = hotspot.position.clone()
            hotspot.getWorldPosition(screenPosition)
            screenPosition.project(camera)

            const translateX = screenPosition.x * sizes.width * 0.5
            const translateY = -screenPosition.y * sizes.height * 0.5

            const labelId = `label-${hotspot.name}`
            const element = document.getElementById(labelId)

            const worldPosition = new THREE.Vector3()
            hotspot.getWorldPosition(worldPosition)
            const direction = worldPosition.clone().sub(camera.position).normalize()
            const distanceToHotspot = camera.position.distanceTo(worldPosition)

            raycaster.set(camera.position, direction)
            
            // Sicherstellen, dass intersectObjects nicht crasht, falls model noch lädt
            const intersects = model ? raycaster.intersectObjects([model], true) : []

            if (element) {
                if (intersects.length > 0) {
                    const distanceToModel = intersects[0].distance
                    if (distanceToModel < distanceToHotspot) {
                        element.style.opacity = '0'
                    } else {
                        element.style.opacity = '1'
                    }
                } else {
                    element.style.opacity = '1'
                }
                element.style.transform = `translate(-50%, -50%) translate(${translateX}px, ${translateY}px) translateY(-20px)`
            }
        })
    }

    renderer.render(scene, camera)
    window.requestAnimationFrame(tick)
}
tick()


// Experience aktivieren
function activateExperience() {
    if (controls.enabled) return

    gsap.to(fadeElements, {
        opacity: 0,
        y: -20,
        duration: 0.8,
        stagger: 0.2,
        onComplete: () => {
            fadeElements.forEach(el => el.style.display = 'none')
        }
    })

    gsap.to(camera.position, {
        x: 0,
        y: 2,
        z: 5,
        duration: 2,
        ease: 'power2.inOut'
    })

    const targetLookAt = new THREE.Vector3(0, 0, 0)
    gsap.to({}, {
        duration: 2,
        onUpdate: () => {
            camera.lookAt(targetLookAt)
        }
    })

    setTimeout(() => {
        if (!isTourMode) {
            hotspotsGroup.visible = true
            gsap.to(hotspotLabels, { opacity: 1, duration: 1 })
        }
    }, 2000)

    setTimeout(() => {
        controls.enabled = true
        canvas.style.cursor = 'grab'
    }, 2000)
}

function handleHotspotClick(hotspot) {
    const worldPosition = new THREE.Vector3()
    hotspot.getWorldPosition(worldPosition)

    const offset = hotspot.cameraOffset || { x: 0, y: 0, z: 2 }

    gsap.to(hotspotLabels, { opacity: 0, duration: 0.5, pointerEvents: 'none' })
    gsap.to(hotspotMaterial, {
        opacity: 0, duration: 0.5, onComplete: () => {
            hotspotsGroup.visible = false
        }
    })

    gsap.to(camera.position, {
        x: worldPosition.x + offset.x,
        y: worldPosition.y + offset.y,
        z: worldPosition.z + offset.z,
        duration: 1.5,
        ease: 'power2.inOut'
    })

    gsap.to(controls.target, {
        x: worldPosition.x,
        y: worldPosition.y,
        z: worldPosition.z,
        duration: 1.5,
        onUpdate: () => {
            camera.lookAt(worldPosition)
        }
    })

    backButton.style.display = 'block'
    gsap.fromTo(backButton,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }
    )

    currentHotspotKey = hotspot.name
    currentStepIndex = 0

    updateInfoWindow()
    infoWindow.classList.add('active')
}

// Info/Anleitungsfenster aktualisieren
function updateInfoWindow() {
    let step

    if (isTourMode) {
        const currentIndex = tourKeys.indexOf(currentTourStepKey)
        const totalSteps = tourKeys.length

        if (currentTourStepKey === 'finish') {
            infoTitle.innerText = "Tour beendet!"
            infoInfo.style.display = 'none'
            infoText.innerText = "Du hast alle Schritte erfolgreich abgeschlossen. Du kannst nun den Drucker frei erkunden oder die Tour über den 'Zurück zum Modell'-Button unten beenden."
            videoContainer.style.display = 'none'
            nextBtn.style.display = 'none'
            step_indicator.innerText = "Fertig!"
            backButton.classList.add('pulse-active')
            prevBtn.disabled = false
        } else {
            step = tourData[currentTourStepKey]
            videoContainer.style.display = 'block'
            nextBtn.style.display = 'block'
            infoInfo.style.display = 'block'
            infoInfo.innerText = step.info

            infoTitle.innerText = step.title
            infoText.innerText = step.text
            infoVideo.src = step.video

            step_indicator.innerText = `${currentIndex + 1} / ${totalSteps}`
            prevBtn.disabled = (currentIndex === 0)
            backButton.classList.remove('pulse-active')
        }
    } else {
        const hotspot = hotspotData[currentHotspotKey]
        const steps = Object.values(hotspot)
        step = steps[currentStepIndex]

        videoContainer.style.display = 'block'
        nextBtn.style.display = 'block'
        infoInfo.style.display = 'none'

        infoTitle.innerText = step.title
        infoText.innerText = step.text
        infoVideo.src = step.video

        step_indicator.innerText = `${currentStepIndex + 1} / ${steps.length}`
        prevBtn.disabled = (currentStepIndex === 0)
        nextBtn.disabled = (currentStepIndex === steps.length - 1)
    }

    if (step && step.video && videoContainer.style.display !== 'none') {
        infoVideo.load()
        infoVideo.play()
    }
}

// Kameraposition ändern bei Tour
function moveCameraToTourStep(stepKey) {
    let targetHotspot;

    if (stepKey.includes('filament_ausladen')) {
        targetHotspot = hotspotsGroup.children.find(h => h.name === 'filament_ausladen')
    } else if (stepKey.includes('filament_einladen')) {
        targetHotspot = hotspotsGroup.children.find(h => h.name === 'filament_einladen')
    } else if (stepKey.includes('druck')) {
        targetHotspot = hotspotsGroup.children.find(h => h.name === 'druckplatte')
    } else if (stepKey.includes('display')) {
        targetHotspot = hotspotsGroup.children.find(h => h.name === 'display')
    }

    if (targetHotspot) {
        const targetPos = targetHotspot.position
        const offset = targetHotspot.cameraOffset || { x: 0, y: 0, z: 2 }

        gsap.to(camera.position, {
            x: targetPos.x + offset.x,
            y: targetPos.y + offset.y,
            z: targetPos.z + offset.z,
            duration: 1.5,
            ease: 'power2.inOut'
        })
        gsap.to(controls.target, {
            x: targetPos.x,
            y: targetPos.y,
            z: targetPos.z,
            duration: 1.5
        })
    }
}

/*
 * Alle Event Listener
 */

// Resize
window.addEventListener('resize', () => {
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

// Klick auf Hotspot/Modell
window.addEventListener('click', (event) => {
    mouse.x = (event.clientX / sizes.width) * 2 - 1
    mouse.y = - (event.clientY / sizes.height) * 2 + 1

    raycaster.setFromCamera(mouse, camera)

    if (model && !controls.enabled) {
        const intersects = raycaster.intersectObject(model, true)
        if (intersects.length > 0) {
            activateExperience()
        }
    }

    if (controls.enabled) {
        const intersects = raycaster.intersectObjects(hotspotsGroup.children, true)
        if (intersects.length > 0) {
            const clickedHotspot = intersects[0].object
            handleHotspotClick(clickedHotspot)
        }
    }
})

// Klick auf zurück Button nach Hotspotklick
backButton.addEventListener('click', (event) => {
    isTourMode = false
    backButton.classList.remove('pulse-active')
    videoContainer.style.display = 'block'
    nextBtn.style.display = 'block'
    event.stopPropagation()

    gsap.to(camera.position, { x: 0, y: 2, z: 5, duration: 1.5, ease: 'power2.inOut' })
    gsap.to(controls.target, { x: 0, y: 0, z: 0, duration: 1.5 })

    if (infoWindow) {
        infoWindow.classList.remove('active')
    }
    if (infoVideo) {
        infoVideo.pause()
    }

    hotspotsGroup.visible = true
    gsap.to(hotspotMaterial, { opacity: 0.75, duration: 0.8 })
    gsap.to(hotspotLabels, { opacity: 1, duration: 0.8, pointerEvents: 'auto' })

    gsap.to(backButton, {
        opacity: 0,
        y: 20,
        duration: 0.4,
        onComplete: () => {
            backButton.style.display = 'none'
        }
    })
})

// Klick auf Weiter-Button
document.getElementById('next-step').addEventListener('click', () => {
    if (isTourMode) {
        const currentIndex = tourKeys.indexOf(currentTourStepKey)

        if (currentIndex < tourKeys.length - 1) {
            currentTourStepKey = tourKeys[currentIndex + 1]
            updateInfoWindow()
            moveCameraToTourStep(currentTourStepKey)
        } else if (currentTourStepKey !== 'finish') {
            currentTourStepKey = 'finish'
            updateInfoWindow()
        }
    } else {
        const steps = Object.keys(hotspotData[currentHotspotKey])
        if (currentStepIndex < steps.length - 1) {
            currentStepIndex++
            updateInfoWindow()
        }
    }
})

// Klick auf Zurück-Button
document.getElementById('prev-step').addEventListener('click', () => {
    nextBtn.style.display = 'block'

    if (isTourMode) {
        if (currentTourStepKey === 'finish') {
            currentTourStepKey = tourKeys[tourKeys.length - 1]
            updateInfoWindow()
            moveCameraToTourStep(currentTourStepKey)
        } else {
            const currentIndex = tourKeys.indexOf(currentTourStepKey)
            if (currentIndex > 0) {
                currentTourStepKey = tourKeys[currentIndex - 1]
                updateInfoWindow()
                moveCameraToTourStep(currentTourStepKey)
            }
        }
    } else {
        if (currentStepIndex > 0) {
            currentStepIndex--
            updateInfoWindow()
        }
    }
})

// Klick auf Geführte Anleitung in der Navigation
tourBtn.addEventListener('click', () => {
    isTourMode = true
    currentTourStepKey = tourKeys[0]
    nextBtn.disabled = false

    hotspotsGroup.visible = false
    hotspotLabels.forEach(label => label.style.opacity = '0')

    videoContainer.style.display = 'block'
    nextBtn.style.display = 'block'

    activateExperience()

    setTimeout(() => {
        updateInfoWindow()
        infoWindow.classList.add('active')
        backButton.style.display = 'block'
        gsap.to(backButton, { opacity: 1, y: 0, duration: 0.6 })

        moveCameraToTourStep(currentTourStepKey)
    }, 2100)
})